import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import moment from "moment";
import "react-calendar/dist/Calendar.css";
import "./Calendar.css";
import DoctorChip from "./DoctorChip";
import * as ApiService from "../services/ApiService";
import { BsCalendarMonth, BsTable, BsDownload, BsLock } from "react-icons/bs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ProposedScheduleModal from "./ProposedScheduleModal";
import { useAuth0 } from "@auth0/auth0-react";
import PremiumRibbon from "./PremiumRibbon";
import Tooltip from "./Tooltip";

const ConfirmationModal = ({
  isOpen,
  message,
  onConfirm,
  onCancel,
  confirmText = "Yes, Continue",
  cancelText = "Cancel",
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay confirmation-overlay">
      <div className="modal-content confirmation-modal">
        <h3 className="confirmation-title">Warning</h3>
        {React.isValidElement(message) ? (
          message
        ) : (
          <p className="confirmation-message">{message}</p>
        )}
        <div className="modal-actions">
          <button onClick={onCancel} className="button-secondary">
            {cancelText}
          </button>
          <button onClick={onConfirm} className="button-primary">
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const CallCalendar = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const isPremiumUser =
    user &&
    user[`https://calllistscheduler.netlify.app/roles`]?.includes("premium");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [displayMonth, setDisplayMonth] = useState(new Date());
  const [isSeeding, setIsSeeding] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [historicalWeights, setHistoricalWeights] = useState({});
  const [showHistoricalWeights, setShowHistoricalWeights] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [viewMode, setViewMode] = useState("calendar"); // 'calendar' or 'roster'
  const [showProposedSchedule, setShowProposedSchedule] = useState(false);
  const [proposedSchedule, setProposedSchedule] = useState(null);
  const [isPrepopulating, setIsPrepopulating] = useState(false);
  const [violationWarning, setViolationWarning] = useState(null);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "ArrowLeft") {
        handlePrevMonth();
      } else if (event.key === "ArrowRight") {
        handleNextMonth();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []); // Empty dependency array since handlePrevMonth and handleNextMonth are stable

  const fetchHolidays = async (date) => {
    try {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString();
      const data = await ApiService.fetchHolidays(
        getAccessTokenSilently,
        year,
        month
      );
      setHolidays(data.map((h) => moment(h.date).toDate()));
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const fetchSchedule = async (date) => {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const startDate = moment([year, month - 1])
        .startOf("month")
        .format("YYYY-MM-DD");
      const endDate = moment([year, month - 1])
        .endOf("month")
        .format("YYYY-MM-DD");
      const scheduleData = await ApiService.fetchSchedule(
        getAccessTokenSilently,
        startDate,
        endDate
      );

      // Transform the flat array into a map grouped by date
      const scheduleMap = scheduleData.reduce((acc, assignment) => {
        if (!acc[assignment.date]) {
          acc[assignment.date] = [];
        }
        acc[assignment.date].push({
          id: assignment.id,
          callList: {
            callListId: assignment.callListId,
            name: assignment.callListName,
          },
          doctor: {
            staffId: assignment.staffId,
            abbreviation: assignment.doctorAbbreviation,
          },
        });
        return acc;
      }, {});

      setSchedule(scheduleMap);

      // If there's no schedule and we're in roster view, switch to calendar view
      if (Object.keys(scheduleMap).length === 0 && viewMode === "roster") {
        setViewMode("calendar");
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
      // If there's an error fetching schedule, switch to calendar view
      if (viewMode === "roster") {
        setViewMode("calendar");
      }
    }
  };

  // New function to fetch historical weights
  const fetchHistoricalWeightsForMonth = async (date) => {
    try {
      const monthStartDate = moment(date).startOf("month").format("YYYY-MM-DD");
      const weightsData = await ApiService.fetchHistoricalWeights(
        getAccessTokenSilently,
        monthStartDate
      );
      setHistoricalWeights(weightsData || {}); // Ensure it's an object
    } catch (error) {
      console.error("Error fetching historical weights:", error);
      setHistoricalWeights({}); // Set to empty object on error
    }
  };

  useEffect(() => {
    fetchHolidays(displayMonth);
    fetchSchedule(displayMonth);
    fetchHistoricalWeightsForMonth(displayMonth); // Fetch weights when month changes
  }, [displayMonth]);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    const dateStr = moment(newDate).format("YYYY-MM-DD");
    const daySchedule = schedule[dateStr];
    setSelectedSchedule(daySchedule);

    setTimeout(() => {
      if (daySchedule) {
        const scheduleElement = document.querySelector(
          ".selected-day-schedule"
        );
        if (scheduleElement) {
          scheduleElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    }, 0);
  };

  const handlePrevMonth = () => {
    setDisplayMonth((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
    setSelectedSchedule(null);
  };

  const handleNextMonth = () => {
    setDisplayMonth((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
    setSelectedSchedule(null);
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
  };

  const isHoliday = (date) => {
    return holidays.some(
      (holiday) =>
        moment(holiday).format("YYYY-MM-DD") ===
        moment(date).format("YYYY-MM-DD")
    );
  };

  const getTileClassName = ({ date }) => {
    if (isHoliday(date)) return "holiday";
    if (isWeekend(date)) return "weekend";
    return "";
  };

  const handleDestroySchedules = async () => {
    try {
      setIsSeeding(true);
      const startDate = moment(displayMonth)
        .startOf("month")
        .format("YYYY-MM-DD");
      const endDate = moment(displayMonth).endOf("month").format("YYYY-MM-DD");
      // Only clear the schedule for the selected month
      await ApiService.destroySchedules(
        getAccessTokenSilently,
        startDate,
        endDate
      );
      alert(
        `Successfully cleared schedule for ${moment(displayMonth).format(
          "MMMM YYYY"
        )}!`
      );
      // Refresh schedule since we cleared it
      fetchSchedule(displayMonth);
    } catch (error) {
      alert("Error clearing schedule: " + error.message);
    } finally {
      setIsSeeding(false);
    }
  };

  const handleGenerateSchedule = async () => {
    try {
      // First show pre-population modal
      setIsPrepopulating(true);
      setShowProposedSchedule(true);
    } catch (error) {
      console.error("Error starting pre-population:", error);
      setIsPrepopulating(false);
      setShowProposedSchedule(false);
    }
  };

  // Helper function to call the actual generate schedule API
  const callGenerateScheduleAPI = async (
    startDate,
    endDate,
    prepopulatedDataForApi
  ) => {
    try {
      const generatedData = await ApiService.generateSchedule(
        getAccessTokenSilently,
        startDate,
        endDate,
        prepopulatedDataForApi
      );
      if (generatedData.hasViolations) {
        setViolationWarning({
          onConfirm: () => {
            setProposedSchedule(generatedData.proposedSchedule);
            setViolationWarning(null);
            setIsGenerating(false); // Also stop loading indicator
          },
          onCancel: () => {
            setViolationWarning(null);
            setShowProposedSchedule(false); // Close main modal
            setIsGenerating(false);
          },
        });
      } else {
        setProposedSchedule(generatedData.proposedSchedule);
        // Update the local state with the new schedule to reflect changes immediately
        fetchSchedule(displayMonth);
        setIsGenerating(false); // Stop loading indicator
      }
    } catch (error) {
      alert("Error generating schedule: " + error.message);
      setIsGenerating(false);
    }
  };

  // New function to handle generation after pre-population
  const generateWithPrepopulated = (prepopulatedSchedule) => {
    const startDate = moment(displayMonth)
      .startOf("month")
      .format("YYYY-MM-DD");
    const endDate = moment(displayMonth).endOf("month").format("YYYY-MM-DD");

    // Set up the loading state
    setIsPrepopulating(false);
    setIsGenerating(true); // This shows the loading modal

    // Call the API to start generation
    callGenerateScheduleAPI(startDate, endDate, prepopulatedSchedule);
  };

  const handleFinalizeSchedule = async (finalSchedule) => {
    const startDate = moment(displayMonth)
      .startOf("month")
      .format("YYYY-MM-DD");
    const endDate = moment(displayMonth).endOf("month").format("YYYY-MM-DD");
    try {
      await ApiService.finalizeSchedule(
        getAccessTokenSilently,
        startDate,
        endDate,
        finalSchedule
      );
      setShowProposedSchedule(false);
      fetchSchedule(displayMonth); // Refresh to show the finalized schedule
      alert("Schedule finalized successfully!");
    } catch (error) {
      console.error("Error finalizing schedule:", error);
      alert("Failed to finalize schedule: " + error.message);
    }
  };

  const groupDoctorsByCallType = (daySchedule) => {
    if (!daySchedule) return {};

    // Group assignments by call list name
    return daySchedule.reduce((acc, assignment) => {
      const callListName = assignment.callList.name;
      if (!acc[callListName]) {
        acc[callListName] = [];
      }

      acc[callListName].push({
        staffId: assignment.doctor.staffId,
        abbreviation: assignment.doctor.abbreviation,
      });

      return acc;
    }, {});
  };

  const formatWeightDisplay = (weightValue) => {
    if (weightValue === undefined || weightValue === null) return "";
    const num = Number(weightValue);
    // Show at least one decimal, preserve up to two if they exist
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    });
  };

  const RosterView = () => {
    const daysInMonth = moment(displayMonth).daysInMonth();
    const monthStart = moment(displayMonth).startOf("month");
    const scheduleArray = [];

    // Generate array of days with their schedules
    for (let i = 0; i < daysInMonth; i++) {
      const currentDate = moment(monthStart).add(i, "days");
      const dateStr = currentDate.format("YYYY-MM-DD");
      const daySchedule = schedule[dateStr] || [];

      // Group doctors by call list
      const doctorsByCallList = groupDoctorsByCallType(daySchedule);

      scheduleArray.push({
        date: dateStr,
        day: currentDate.format("ddd"),
        schedule: doctorsByCallList,
      });
    }

    // Get unique call list names from the entire month's schedule
    const allCallLists = new Set();
    Object.values(schedule).forEach((daySchedule) => {
      Object.keys(groupDoctorsByCallType(daySchedule)).forEach((callList) => {
        allCallLists.add(callList);
      });
    });
    // Sort call list names in reverse lexicographical order
    const callListNames = Array.from(allCallLists).sort();

    return (
      <div className="roster-view">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Day</th>
              {callListNames.map((callList) => (
                <th key={callList}>{callList}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scheduleArray.map(({ date, day, schedule }) => (
              <tr
                key={date}
                className={
                  isHoliday(new Date(date))
                    ? "holiday"
                    : moment(date).day() === 0 || moment(date).day() === 6
                    ? "weekend"
                    : ""
                }
              >
                <td>{date}</td>
                <td>{day}</td>
                {callListNames.map((callList) => (
                  <td key={`${date}-${callList}`}>
                    <div className="roster-doctors">
                      {schedule[callList]?.map((doctor, idx) => (
                        <span key={idx} className="doctor-chip-with-weight">
                          <DoctorChip doctor={doctor} mini={true} />
                          {showHistoricalWeights &&
                            historicalWeights[doctor.staffId] !== undefined && (
                              <span
                                className="scheduling-weight"
                                data-weight={historicalWeights[doctor.staffId]}
                              >
                                {formatWeightDisplay(
                                  historicalWeights[doctor.staffId]
                                )}
                              </span>
                            )}
                        </span>
                      )) || "-"}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const handleDownloadPDF = () => {
    // Create new PDF document
    const doc = new jsPDF();

    // Get page width for centering
    const pageWidth = doc.internal.pageSize.width;
    const margins = 25; // Increased side margins

    // Add title with smaller font and centered
    const title = `${moment(displayMonth)
      .format("MMMM YYYY")
      .toUpperCase()} CALL LIST`;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    const titleWidth =
      (doc.getStringUnitWidth(title) * doc.internal.getFontSize()) /
      doc.internal.scaleFactor;
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(title, titleX, 25);

    // Reset font to normal for the rest of the document
    doc.setFont("helvetica", "normal");

    // Add legend with smaller size and centered
    doc.setFontSize(8);
    doc.setDrawColor(0);

    // Calculate legend items total width for centering
    const weekendBoxWidth = 6;
    const weekendTextWidth =
      (doc.getStringUnitWidth("Weekends") * doc.internal.getFontSize()) /
      doc.internal.scaleFactor;
    const holidayBoxWidth = 6;
    const holidayTextWidth =
      (doc.getStringUnitWidth("Public Holidays") * doc.internal.getFontSize()) /
      doc.internal.scaleFactor;
    const spaceBetweenLegend = 20;
    const totalLegendWidth =
      weekendBoxWidth +
      weekendTextWidth +
      spaceBetweenLegend +
      holidayBoxWidth +
      holidayTextWidth;

    const legendStartX = (pageWidth - totalLegendWidth) / 2;

    // Draw weekend legend
    doc.setFillColor(230, 243, 255); // weekend color #e6f3ff
    doc.rect(legendStartX, 32, 6, 6, "F");
    doc.text("Weekends", legendStartX + weekendBoxWidth + 2, 36);

    // Draw holiday legend
    doc.setFillColor(255, 243, 224); // holiday color #fff3e0
    const holidayStartX =
      legendStartX + weekendBoxWidth + weekendTextWidth + spaceBetweenLegend;
    doc.rect(holidayStartX, 32, 6, 6, "F");
    doc.text("Public Holidays", holidayStartX + holidayBoxWidth + 2, 36);

    // Get all unique call list names
    const allCallLists = new Set();
    Object.values(schedule).forEach((daySchedule) => {
      Object.keys(groupDoctorsByCallType(daySchedule)).forEach((callList) => {
        allCallLists.add(callList);
      });
    });
    // Sort call list names in reverse lexicographical order
    const callListNames = Array.from(allCallLists).sort();

    // Prepare table data
    const daysInMonth = moment(displayMonth).daysInMonth();
    const monthStart = moment(displayMonth).startOf("month");
    const tableData = [];

    for (let i = 0; i < daysInMonth; i++) {
      const currentDate = moment(monthStart).add(i, "days");
      const dateStr = currentDate.format("YYYY-MM-DD");
      const daySchedule = schedule[dateStr] || [];
      const doctorsByCallList = groupDoctorsByCallType(daySchedule);

      const row = [
        dateStr,
        currentDate.format("ddd"),
        ...callListNames.map(
          (callList) =>
            (doctorsByCallList[callList] || [])
              .map((doctor) => doctor.abbreviation)
              .join(", ") || "-"
        ),
      ];

      tableData.push({
        content: row,
        isHoliday: isHoliday(new Date(dateStr)),
        isWeekend: currentDate.day() === 0 || currentDate.day() === 6,
      });
    }

    // Configure the table with tighter spacing and margins
    doc.autoTable({
      startY: 45,
      margin: { left: margins, right: margins },
      head: [["Date", "Day", ...callListNames]],
      body: tableData.map((row) => row.content),
      didParseCell: function (data) {
        // Skip coloring if it's a header cell
        if (data.section === "head") return;

        const row = tableData[data.row.index];
        if (row) {
          if (row.isHoliday) {
            data.cell.styles.fillColor = [255, 243, 224];
          } else if (row.isWeekend) {
            data.cell.styles.fillColor = [230, 243, 255];
          }
        }
      },
      styles: {
        fontSize: 8, // Smaller font size
        cellPadding: 1, // Reduced padding
        lineHeight: 1.1, // Tighter line height
      },
      headStyles: {
        fillColor: [240, 247, 240],
        textColor: 51,
        fontSize: 9, // Slightly larger than body but still compact
        fontStyle: "bold",
        cellPadding: 1.5, // Slightly more padding for header
      },
      theme: "grid",
    });

    // Save the PDF
    doc.save(`call-list-${moment(displayMonth).format("YYYY-MM")}.pdf`);
  };

  const handleDownloadCSV = () => {
    // Get all unique call list names
    const allCallLists = new Set();
    Object.values(schedule).forEach((daySchedule) => {
      Object.keys(groupDoctorsByCallType(daySchedule)).forEach((callList) => {
        allCallLists.add(callList);
      });
    });
    // Sort call list names in reverse lexicographical order
    const callListNames = Array.from(allCallLists).sort();

    // Prepare CSV data
    const daysInMonth = moment(displayMonth).daysInMonth();
    const monthStart = moment(displayMonth).startOf("month");

    // Create header row
    const csvRows = [["Date", "Day", ...callListNames, "Day Type"].join(",")];

    // Create data rows
    for (let i = 0; i < daysInMonth; i++) {
      const currentDate = moment(monthStart).add(i, "days");
      const dateStr = currentDate.format("YYYY-MM-DD");
      const daySchedule = schedule[dateStr] || [];
      const doctorsByCallList = groupDoctorsByCallType(daySchedule);

      // Determine day type using exact holiday dates from database
      let dayType = "Regular Weekday";
      const isDateWeekend = currentDate.day() === 0 || currentDate.day() === 6;
      const isDateHoliday = holidays.some(
        (holiday) => moment(holiday).format("YYYY-MM-DD") === dateStr
      );

      if (isDateHoliday) {
        dayType = "Public Holiday";
      } else if (isDateWeekend) {
        dayType = "Weekend";
      }

      const row = [
        dateStr,
        currentDate.format("ddd"),
        ...callListNames.map(
          (callList) =>
            (doctorsByCallList[callList] || [])
              .map((doctor) => doctor.abbreviation)
              .join(", ") || "-"
        ),
        dayType,
      ];

      csvRows.push(row.join(","));
    }

    // Create and download CSV file
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `call-list-${moment(displayMonth).format("YYYY-MM")}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="page-container">
      <div className="staff-header">
        <h2>Schedule Management</h2>
      </div>

      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={handlePrevMonth} className="nav-button">
            {"<"}
          </button>
          <h2 className="current-month">
            {moment(displayMonth).format("MMMM YYYY")}
          </h2>
          <button onClick={handleNextMonth} className="nav-button">
            {">"}
          </button>
        </div>
      </div>

      <div className="calendar-legend">
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color weekend-color"></div>
            <span>Weekends</span>
          </div>
          <div className="legend-item">
            <div className="legend-color holiday-color"></div>
            <span>Public Holidays</span>
          </div>
        </div>
        {Object.keys(schedule).length > 0 ? (
          <div className="schedule-buttons">
            <button
              className="generate-button"
              onClick={handleDestroySchedules}
              disabled={isSeeding}
            >
              {isSeeding ? "Clearing..." : "Clear Schedule"}
            </button>
            {isPremiumUser ? (
              <button
                className="generate-button"
                onClick={() => {
                  setProposedSchedule(schedule);
                  setShowProposedSchedule(true);
                }}
              >
                Edit Schedule
              </button>
            ) : (
              <div className="premium-feature-container">
                <Tooltip>
                  <button className="generate-button premium-locked" disabled>
                    <BsLock className="lock-icon" /> Edit Schedule
                    <PremiumRibbon />
                  </button>
                </Tooltip>
              </div>
            )}
            <button
              className="generate-button"
              onClick={handleGenerateSchedule}
              disabled={isGenerating}
            >
              Re-generate Schedule
            </button>
          </div>
        ) : (
          <button
            className="generate-button"
            onClick={handleGenerateSchedule}
            disabled={isGenerating}
          >
            Generate Schedule for {moment(displayMonth).format("MMMM YYYY")}
          </button>
        )}
      </div>

      {Object.keys(schedule).length > 0 && (
        <div className="view-toggle-container">
          <div className="download-buttons-group">
            <button
              className="view-button download-button"
              onClick={handleDownloadPDF}
            >
              <BsDownload /> PDF
            </button>
            <button
              className="view-button download-button"
              onClick={handleDownloadCSV}
            >
              <BsDownload /> CSV
            </button>
          </div>

          <div className="view-controls-right-column">
            <div className="display-options-group">
              <label className="toggle-switch-label">
                <input
                  type="checkbox"
                  checked={showHistoricalWeights}
                  onChange={() =>
                    setShowHistoricalWeights(!showHistoricalWeights)
                  }
                  className="toggle-switch-checkbox"
                />
                See historical weights
                <span className="toggle-switch-slider"></span>
              </label>
            </div>

            <div className="view-toggle-group">
              <button
                className={`view-button ${
                  viewMode === "calendar" ? "active" : ""
                }`}
                onClick={() => setViewMode("calendar")}
              >
                <BsCalendarMonth /> Calendar
              </button>
              <button
                className={`view-button ${
                  viewMode === "roster" ? "active" : ""
                }`}
                onClick={() => setViewMode("roster")}
              >
                <BsTable /> Roster
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === "calendar" ? (
        <>
          <Calendar
            onChange={handleDateChange}
            value={selectedDate}
            activeStartDate={displayMonth}
            onActiveStartDateChange={({ activeStartDate }) => {
              setDisplayMonth(activeStartDate);
              setSelectedSchedule(null);
            }}
            defaultView="month"
            minDetail="month"
            maxDetail="month"
            prev2Label={null}
            next2Label={null}
            prevLabel={null}
            nextLabel={null}
            showNeighboringMonth={false}
            calendarType="gregory"
            formatShortWeekday={(locale, date) =>
              moment(date).format("ddd").toUpperCase()
            }
            navigationLabel={() => ""}
            tileClassName={getTileClassName}
            tileContent={({ date, view }) => {
              if (view === "month") {
                const dateStr = moment(date).format("YYYY-MM-DD");
                const daySchedule = schedule[dateStr];
                if (daySchedule) {
                  const groupedSchedule = groupDoctorsByCallType(daySchedule);
                  const sortedCallListNames =
                    Object.keys(groupedSchedule).sort();

                  return (
                    <div className="cell-content">
                      <div className="schedule-content">
                        {sortedCallListNames.map((callListName) => (
                          <div key={callListName} className="call-group">
                            <div className="call-label">{callListName}:</div>
                            <div className="doctor-chips">
                              {groupedSchedule[callListName].map(
                                (doctor, docIdx) => (
                                  <span
                                    key={`${callListName}-${docIdx}`}
                                    className="doctor-chip-with-weight"
                                  >
                                    <DoctorChip doctor={doctor} mini={true} />
                                    {showHistoricalWeights &&
                                      historicalWeights[doctor.staffId] !==
                                        undefined && (
                                        <span
                                          className="scheduling-weight"
                                          data-weight={
                                            historicalWeights[doctor.staffId]
                                          }
                                        >
                                          {formatWeightDisplay(
                                            historicalWeights[doctor.staffId]
                                          )}
                                        </span>
                                      )}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              }
              return null;
            }}
          />
          {selectedSchedule && (
            <div className="selected-day-schedule">
              <div className="selected-day-header">
                Schedule for {moment(selectedDate).format("MMMM D, YYYY")}
              </div>
              <div className="call-groups-container">
                {Object.entries(groupDoctorsByCallType(selectedSchedule)).map(
                  ([callListName, doctors]) => (
                    <div key={callListName} className="selected-day-call-group">
                      <div className="selected-day-call-label">
                        {callListName}:
                      </div>
                      <div className="selected-day-doctors">
                        {doctors.map((doctor, idx) => (
                          <span
                            key={`${callListName}-${idx}`}
                            className="doctor-chip-with-weight"
                          >
                            <DoctorChip doctor={doctor} />
                            {showHistoricalWeights &&
                              historicalWeights[doctor.staffId] !==
                                undefined && (
                                <span
                                  className="scheduling-weight"
                                  data-weight={
                                    historicalWeights[doctor.staffId]
                                  }
                                >
                                  {formatWeightDisplay(
                                    historicalWeights[doctor.staffId]
                                  )}
                                </span>
                              )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <RosterView />
      )}

      <ProposedScheduleModal
        isOpen={showProposedSchedule && !violationWarning}
        onClose={() => {
          setShowProposedSchedule(false);
          setIsGenerating(false);
          setIsPrepopulating(false);
        }}
        proposedSchedule={proposedSchedule}
        startDate={moment(displayMonth).startOf("month").format("YYYY-MM-DD")}
        endDate={moment(displayMonth).endOf("month").format("YYYY-MM-DD")}
        onFinalize={
          isPrepopulating ? generateWithPrepopulated : handleFinalizeSchedule
        }
        holidays={holidays}
        isGenerating={isGenerating}
        isPrepopulating={isPrepopulating}
        resetOnClose={true}
      />
      <ConfirmationModal
        isOpen={!!violationWarning}
        message={
          <div className="confirmation-message">
            <p>
              We were unable to generate a fully compliant schedule with the
              current constraints.
            </p>
            <p>
              Try increasing doctor availability or relaxing rules and trying
              again.
            </p>
            <p>
              Do you want to view the schedule that may have some rule
              violations?
            </p>
          </div>
        }
        onConfirm={violationWarning?.onConfirm}
        onCancel={violationWarning?.onCancel}
      />
    </div>
  );
};

export default CallCalendar;
